import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SystemHeader from '../SystemHeader';
import { ThongDiepAPI } from '../../services/feature-api';
import { Quote, Search, Filter } from 'lucide-react';

const PHONG_CACH_OPTIONS = ['Mạnh mẽ', 'Thơ', 'Đời thường', 'Sâu sắc'];

const PHONG_CACH_STYLE = {
  'Mạnh mẽ': 'bg-red-100 text-red-800',
  'Thơ': 'bg-pink-100 text-pink-800',
  'Đời thường': 'bg-blue-100 text-blue-800',
  'Sâu sắc': 'bg-purple-100 text-purple-800',
};

const ThuVienThongDiepPage = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPhongCach, setFilterPhongCach] = useState('');
  const [filterNhomChuDe, setFilterNhomChuDe] = useState('');

  const loadGrouped = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    const res = await ThongDiepAPI.getGrouped(accessToken);
    setGrouped(res?.success ? (res.data || []) : []);
    setLoading(false);
  }, [accessToken]);

  useEffect(() => { loadGrouped(); }, [loadGrouped]);

  const allNhomChuDe = useMemo(() => grouped.map((g) => g.nhomChuDe), [grouped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return grouped
      .filter((g) => !filterNhomChuDe || g.nhomChuDe === filterNhomChuDe)
      .map((g) => ({
        ...g,
        items: (g.items || []).filter((it) => {
          if (filterPhongCach && it.phongCach !== filterPhongCach) return false;
          if (!q) return true;
          const hay = `${it.cauTrietLy || ''} ${it.goiYSuDung || ''} ${it.nhomChuDe || ''}`.toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [grouped, search, filterPhongCach, filterNhomChuDe]);

  const totalItems = filtered.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Thư viện thông điệp"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="flex-1 w-full max-w-md mx-auto px-3 py-3 space-y-3">
        {/* Hero */}
        <div className="rounded-2xl p-4 text-white shadow-md bg-gradient-to-br from-purple-600 to-pink-500">
          <div className="flex items-center gap-2 mb-1">
            <Quote size={20} />
            <h2 className="font-bold text-base">100 câu triết lý</h2>
          </div>
          <p className="text-xs opacity-90">
            Tìm câu phù hợp để đăng story, caption, hoặc gửi đồng đội khi cần truyền cảm hứng.
          </p>
        </div>

        {/* Search + Filter */}
        <div className="bg-white rounded-2xl shadow-sm border p-3 space-y-2">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo từ khóa..."
              className="flex-1 text-sm bg-transparent outline-none"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterNhomChuDe}
              onChange={(e) => setFilterNhomChuDe(e.target.value)}
              className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none"
            >
              <option value="">Tất cả chủ đề</option>
              {allNhomChuDe.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select
              value={filterPhongCach}
              onChange={(e) => setFilterPhongCach(e.target.value)}
              className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 outline-none"
            >
              <option value="">Tất cả phong cách</option>
              {PHONG_CACH_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Filter size={12} />
            <span>Hiển thị {totalItems} câu</span>
          </div>
        </div>

        {loading && (
          <div className="rounded-2xl bg-white p-6 text-center text-sm text-gray-500 shadow-sm border">
            Đang tải thông điệp...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-white p-6 text-center text-sm text-gray-500 shadow-sm border">
            Không tìm thấy câu nào phù hợp
          </div>
        )}

        {!loading && filtered.map((g) => (
          <div key={g.nhomChuDe} className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 px-1">{g.nhomChuDe}</h3>
            <div className="space-y-2">
              {g.items.map((it) => (
                <div key={it.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
                  <p className="text-sm text-gray-800 leading-relaxed italic">"{it.cauTrietLy}"</p>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {it.phongCach && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${PHONG_CACH_STYLE[it.phongCach] || 'bg-gray-100 text-gray-700'}`}>
                        {it.phongCach}
                      </span>
                    )}
                    {it.ganYodyPhuongAnh && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-800">
                        Yody Phương Anh
                      </span>
                    )}
                  </div>
                  {it.goiYSuDung && (
                    <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                      <p className="text-[11px] text-gray-600">
                        <span className="font-semibold">Gợi ý dùng:</span> {it.goiYSuDung}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThuVienThongDiepPage;
